import React, { useEffect, useState } from 'react';
import Tooltip, { tooltipClasses, TooltipProps } from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import { colors } from '../../constants';

const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: colors.grayTitle,
    color: colors.white,
    fontWeight: 500
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: colors.grayTitle
  }
}));


interface CustomTooltipProps extends TooltipProps {
  children: React.ReactElement;
  openDuration?: number; // in ms, if set, tooltip will auto-hide
}

export const CustomTooltip: React.FC<CustomTooltipProps> = ({
  children,
  title,
  arrow = true,
  openDuration,
  open: controlledOpen,
  ...rest
}) => {
  // If open is controlled, use it. Otherwise, manage hover and auto-hide internally
  const [open, setOpen] = useState<boolean>(false);
  const timerRef = React.useRef<NodeJS.Timeout | null>(null);

  const tooltipOpen = controlledOpen !== undefined ? controlledOpen : open;

  // Only attach hover logic if open prop is not controlled

  const child = React.isValidElement(children) && controlledOpen === undefined
    ? React.cloneElement(children as React.ReactElement<any>, {
      onMouseEnter: (e: React.MouseEvent) => {
        setOpen(true);
        if (openDuration) {
          if (timerRef.current) clearTimeout(timerRef.current);
          timerRef.current = setTimeout(() => setOpen(false), openDuration);
        }
        (children as React.ReactElement<any>).props.onMouseEnter?.(e);
      },
      onMouseLeave: (e: React.MouseEvent) => {
        setOpen(false);
        if (timerRef.current) clearTimeout(timerRef.current);
        (children as React.ReactElement<any>).props.onMouseLeave?.(e);
      },
    })
    : children;

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return (
    <StyledTooltip
      title={title}
      arrow={arrow}
      {...rest}
      open={tooltipOpen}
    >
      {child}
    </StyledTooltip>
  );
};
